import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SparklesIcon, AcademicCapIcon, UserGroupIcon, TrophyIcon, BuildingOffice2Icon, HeartIcon } from '@heroicons/react/24/outline';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import SEO from '../components/SEO';

const Home = () => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(coursesRef, orderBy('featured', 'desc'));
      const querySnapshot = await getDocs(q);
      const coursesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCourses(coursesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
      setLoading(false);
    }
  };

  const displayCourses = courses.slice(0, 3).map(course => ({
    name: course.name,
    duration: course.duration,
    price: course.price,
    highlights: course.content ? course.content.slice(0, 4) : ['Course details available'],
    featured: course.featured || false,
  }));
  const features = [
    { name: t('home.expertMentorship'), description: t('home.expertMentorshipDesc'), icon: UserGroupIcon },
    { name: t('home.handsOnTraining'), description: t('home.handsOnTrainingDesc'), icon: SparklesIcon },
    { name: t('home.lifetimeSupport'), description: t('home.lifetimeSupportDesc'), icon: AcademicCapIcon },
    { name: t('home.certification'), description: t('home.certificationDesc'), icon: TrophyIcon },
  ];


  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Kamal Institute",
    "description": "Premier institute for goldsmithing and silversmithing courses in Ajmer, Rajasthan. Learn handmade jewellery making through immersive, hands-on training with industry experts.",
    "url": "https://kamalinstitute.co.in",
    "logo": "https://kamalinstitute.co.in/logo.png",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Ajmer",
      "addressLocality": "Ajmer",
      "addressRegion": "Rajasthan",
      "postalCode": "",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-97633-03025",
      "contactType": "Customer Service",
      "email": "info@kamalbullion.com",
      "areaServed": "IN",
      "availableLanguage": ["English", "Hindi"]
    },
    "sameAs": [
      "https://kamalbullion.com"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "offerCount": "3",
      "lowPrice": "45000",
      "highPrice": "150000",
      "priceCurrency": "INR"
    }
  };

  return (
    <div className="bg-white">
      <SEO
        title="Master the Art of Jewellery Making - Gold & Silver Smith Courses"
        description="Join India's premier institute for goldsmithing and silversmithing. Experience immersive, hands-on training with industry experts in a fully residential program in Ajmer, Rajasthan."
        keywords="goldsmith course, silversmith course, jewellery making course, handmade jewellery training, jewellery institute India, Ajmer jewellery course, goldsmithing training, silversmithing training, jewellery certification, Kamal Institute, jewellery making classes Rajasthan"
        url="/"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden pt-24 min-h-[32rem] sm:min-h-[36rem]">
        {/* Background image with Gaussian blur */}
        <div className="absolute inset-0 -z-10">
          <img
            src="/ki.jpeg"
            alt=""
            className="h-full w-full object-cover scale-110 blur-sm"
            aria-hidden
          />
        </div>
        {/* Overlay for text readability */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-gray-900/50 via-gray-900/55 to-gray-900/65" aria-hidden />
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl drop-shadow-lg">
              {t('home.heroTitle1')}
              <span className="text-gold-400"> {t('home.heroTitle2')}</span>
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-200">
              {t('home.heroSubtitle')}
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/courses"
                className="rounded-md bg-gold-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-gold-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600 transition-all"
              >
                {t('home.exploreCourses')}
              </Link>
              <Link
                to="/activities"
                className="rounded-md bg-white px-6 py-3 text-base font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:ring-gray-400 transition-all"
              >
                {t('home.viewActivities')}
              </Link>
              <Link
                to="/contact"
                className="text-base font-semibold leading-6 text-white hover:text-gold-400 transition-colors"
              >
                {t('home.contactUs')} <span aria-hidden="true">→</span>
              </Link>
            </div>
            {/* Activities Preview (near Explore Courses) */}
            <div className="mx-auto mt-12 max-w-3xl">
              <div className="rounded-3xl bg-white/70 backdrop-blur-sm ring-1 ring-gray-900/10 p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gold-700">{t('home.activities')}</p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {t('home.activitiesSubtitle')}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-gray-600">
                      {t('home.activitiesDesc')}
                    </p>
                  </div>
                  <Link
                    to="/activities"
                    className="inline-flex items-center justify-center rounded-md bg-gold-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gold-500 transition-colors"
                  >
                    {t('home.exploreActivities')}
                  </Link>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-900/10">
                    <div className="flex items-center gap-2">
                      <HeartIcon className="h-5 w-5 text-gold-600" aria-hidden="true" />
                      <p className="text-sm font-semibold text-gray-900">{t('home.weddings')}</p>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{t('home.weddingsDesc')}</p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-900/10">
                    <div className="flex items-center gap-2">
                      <BuildingOffice2Icon className="h-5 w-5 text-gold-600" aria-hidden="true" />
                      <p className="text-sm font-semibold text-gray-900">{t('home.corporates')}</p>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{t('home.corporatesDesc')}</p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-900/10">
                    <div className="flex items-center gap-2">
                      <SparklesIcon className="h-5 w-5 text-gold-600" aria-hidden="true" />
                      <p className="text-sm font-semibold text-gray-900">{t('home.carnivals')}</p>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{t('home.carnivalsDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-gold-200 to-gold-400 opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
      </div>

      {/* Features Section */}
      <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base font-semibold leading-7 text-gold-600">{t('home.learnBetter')}</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {t('home.whyChoose')}
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            {t('home.whyChooseDesc')}
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-4">
            {features.map((feature) => (
              <div key={feature.name} className="flex flex-col">
                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                  <feature.icon className="h-5 w-5 flex-none text-gold-600" aria-hidden="true" />
                  {feature.name}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">{feature.description}</p>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Courses Preview */}
      <div className="bg-gray-50 py-24 sm:py-32 mt-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{t('home.ourCourses')}</h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t('home.coursesSubtitle')}
            </p>
          </div>
          <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3">
            {loading ? (
              <div className="col-span-3 text-center py-8">
                <p className="text-gray-600">{t('home.loadingCourses')}</p>
              </div>
            ) : displayCourses.length === 0 ? (
              <div className="col-span-3 text-center py-8">
                <p className="text-gray-600">{t('home.noCourses')}</p>
              </div>
            ) : (
              displayCourses.map((course) => (
              <div
                key={course.name}
                className={`flex flex-col justify-between rounded-3xl bg-white p-8 shadow-xl ring-1 ring-gray-900/10 ${
                  course.featured ? 'ring-2 ring-gold-600' : ''
                }`}
              >
                <div>
                  {course.featured && (
                    <p className="mb-4 inline-block rounded-full bg-gold-600 px-4 py-1 text-sm font-semibold text-white">
                      {t('home.mostPopular')}
                    </p>
                  )}
                  <h3 className="text-2xl font-bold tracking-tight text-gray-900">{course.name}</h3>
                  <p className="mt-4 text-sm leading-6 text-gray-600">{course.duration}</p>
                  <p className="mt-6 flex items-baseline gap-x-1">
                    <span className="text-4xl font-bold tracking-tight text-gray-900">{course.price}</span>
                  </p>
                  <ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                    {course.highlights.map((highlight) => (
                      <li key={highlight} className="flex gap-x-3">
                        <span className="text-gold-600">✓</span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/courses"
                  className={`mt-8 block rounded-md px-3.5 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-all ${
                    course.featured
                      ? 'bg-gold-600 text-white hover:bg-gold-500 focus-visible:outline-gold-600'
                      : 'bg-white text-gold-600 ring-1 ring-inset ring-gold-200 hover:ring-gold-300'
                  }`}
                >
                  {t('home.learnMore')}
                </Link>
              </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gold-600">
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t('home.readyToStart')}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gold-100">
              {t('home.readyDesc')}
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <a
                href="https://forms.gle/VvwLJJH4gY94Wwd5A"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-white px-6 py-3 text-base font-semibold text-gold-600 shadow-sm hover:bg-gold-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all"
              >
                {t('home.courseEnquiry')}
              </a>
              <Link
                to="/courses"
                className="text-base font-semibold leading-6 text-white hover:text-gold-100 transition-colors"
              >
                {t('home.viewAllCourses')} <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
